import { useEffect } from "react";

/**
 * SEO meta tags per language — kept in one place.
 * Used by useDocumentMeta to sync <title>, description, og:*, twitter:*, hreflang, canonical.
 */
export const SEO = {
  fr: {
    title: "Marocanada — Immigration Maroc Canada · Conseillers RCIC agréés",
    description:
      "Cabinet d'immigration agréé pour Marocains francophones vers le Canada. Entrée Express Francophone, PRTQ Québec, PNP, Permis d'études vers RP, Visa Startup, Parrainage familial. Consultants RCIC.",
    ogLocale: "fr_FR",
    ogTitle: "Marocanada — Immigration Maroc Canada · Conseillers RCIC agréés",
    ogDescription:
      "Cabinet d'immigration agréé pour Marocains francophones vers le Canada. Entrée Express Francophone, PRTQ, PNP, Permis d'études vers RP.",
    twitterTitle: "Marocanada — Immigration Maroc → Canada",
    twitterDescription:
      "Conseillers RCIC agréés pour Marocains francophones. Entrée Express Francophone, PRTQ, PNP.",
  },
  en: {
    title: "Marocanada — Morocco to Canada Immigration · Licensed RCIC Consultants",
    description:
      "Licensed immigration consultancy for French-speaking Moroccans moving to Canada. Francophone Express Entry, Quebec Skilled Worker (QSWP), PNP, Study Permit to PR, Startup Visa, Family Sponsorship. RCIC consultants.",
    ogLocale: "en_CA",
    ogTitle: "Marocanada — Morocco to Canada Immigration · Licensed RCIC Consultants",
    ogDescription:
      "Licensed immigration consultancy for French-speaking Moroccans moving to Canada. Francophone Express Entry, QSWP, PNP.",
    twitterTitle: "Marocanada — Morocco → Canada Immigration",
    twitterDescription:
      "Licensed RCIC consultants for French-speaking Moroccans. Express Entry Francophone, QSWP, PNP.",
  },
};

const SITE_ORIGIN = "https://marocanada.org";

const setMeta = (selector, attr, value) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

/* Replace all data-managed="seo" link tags then re-emit them. */
const setLinks = (links) => {
  if (typeof document === "undefined") return;
  document.head
    .querySelectorAll('link[data-managed="seo"]')
    .forEach((el) => el.remove());
  links.forEach(({ rel, href, hreflang }) => {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    link.setAttribute("href", href);
    if (hreflang) link.setAttribute("hreflang", hreflang);
    link.setAttribute("data-managed", "seo");
    document.head.appendChild(link);
  });
};

/**
 * useDocumentMeta — keeps <title>, description, og:*, twitter:*,
 * <link rel="canonical">, and <link rel="alternate" hreflang> in sync with the active language and route.
 *
 * Pass { noindex: true } for private pages (e.g. /admin).
 */
const useDocumentMeta = (lang, { noindex = false } = {}) => {
  useEffect(() => {
    const path =
      typeof window !== "undefined" ? window.location.pathname || "/" : "/";
    const canonical = `${SITE_ORIGIN}${path === "/" ? "/" : path.replace(/\/$/, "")}`;

    if (noindex) {
      document.title = "Admin · Marocanada";
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
      // No hreflang for noindex pages — just a canonical
      setLinks([{ rel: "canonical", href: canonical }]);
      return;
    }

    const s = SEO[lang] || SEO.fr;
    document.title = s.title;
    setMeta('meta[name="description"]', "content", s.description);
    setMeta('meta[property="og:locale"]', "content", s.ogLocale);
    setMeta('meta[property="og:title"]', "content", s.ogTitle);
    setMeta('meta[property="og:description"]', "content", s.ogDescription);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", s.twitterTitle);
    setMeta('meta[name="twitter:description"]', "content", s.twitterDescription);
    setMeta('meta[name="robots"]', "content", "index, follow, max-snippet:-1, max-image-preview:large");
    document.documentElement.lang = lang;

    // hreflang — both FR and EN point to the same URL (client-side toggle)
    setLinks([
      { rel: "canonical", href: canonical },
      { rel: "alternate", href: canonical, hreflang: "fr" },
      { rel: "alternate", href: canonical, hreflang: "fr-ma" },
      { rel: "alternate", href: canonical, hreflang: "fr-ca" },
      { rel: "alternate", href: canonical, hreflang: "en" },
      { rel: "alternate", href: canonical, hreflang: "en-ca" },
      { rel: "alternate", href: canonical, hreflang: "x-default" },
    ]);
  }, [lang, noindex]);
};

export default useDocumentMeta;
